<script setup lang="ts">
import StatePendingMoodImage from 'assets/modal-programme-application-rejected.png';
import { match } from 'ts-pattern';
import type { PublicMerchant } from '~~/models/Merchant';
import type { PublicProgramme } from '~~/models/Programme';
import { programmeApplicationRejectionReasonSchema, type ProgrammeApplicationRejectionReason } from '~~/models/ProgrammeApplication';

const props = defineProps<{
	programme: PublicProgramme;
	merchant: PublicMerchant;
}>();

const open = defineModel<boolean>('open');

const { t } = useI18n();

const title = computed((): string => {
	const reason: ProgrammeApplicationRejectionReason = programmeApplicationRejectionReasonSchema.safeParse(props.programme.rejectionReason).data || 'general';

	return match(reason)
		.with('general', () => t('modal.programmeApplication.rejected.reason.general.title', [props.programme.name]))
		.with('policy', () => t('modal.programmeApplication.rejected.reason.policy.title', [props.programme.name]))
		.with('reach', () => t('modal.programmeApplication.rejected.reason.reach.title', [props.programme.name]))
		.with('topic', () => t('modal.programmeApplication.rejected.reason.topic.title', [props.programme.name]))
		.exhaustive();
});
</script>

<template>
	<UModal
		v-model:open="open"
		:ui="{ content: 'w-full lg:max-w-4xl overflow-auto' }"
		:title="title"
	>
		<template #content="{ close }">
			<div class="p-8 lg:p-16">
				<UButton
					icon="i-lucide-x"
					class="absolute top-4 right-4"
					variant="ghost"
					color="neutral"
					@click="close"
				/>
				<div class="lg:flex items-center justify-center gap-16">
					<img
						:src="StatePendingMoodImage"
						class="size-60 mb-8 lg:mb-0 dark:invert-90"
						alt="Wir freuen uns auf deinen Content"
					>
					<div>
						<div class="mb-8">
							<UColorModeImage
								v-if="merchant && merchant.logoUrl"
								:light="merchant.logoUrl"
								:dark="merchant.darkModeLogoUrl || merchant.logoUrl"
								:alt="merchant.name"
								class="h-7 w-auto"
							/>
							<img
								v-else
								class="h-8 w-auto"
								src="/clickfire_logo_amber_500.svg"
								alt="Clickfire"
							>
						</div>
						<p class="text-xl font-bold mb-4">
							{{ title }}
						</p>
						<p class="text-sm text-muted italic mb-8">
							{{ $t('modal.programmeApplication.rejected.note') }}
						</p>
						<UButton @click="close">
							{{ $t('modal.programmeApplication.button.confirm') }}
						</UButton>
					</div>
				</div>
			</div>
		</template>
	</UModal>
</template>
